import * as Sentry from '@sentry/react';
import {
  getBusinessCode,
  getBusinessStatus,
  isStandardBusinessSuccess,
} from '../request-response';

/**
 * 精简版 Sentry 初始化配置
 * 只专注于错误上报功能
 */
export function initSentry(dsn: string, environment: string = 'development') {
  if (__DEV__) {
    console.log('🚀 开始初始化 Sentry...', {dsn, environment});
  }

  Sentry.init({
    dsn,
    environment,
    release: 'singam@1.0.0',
    debug: false, // 关闭 Sentry 调试日志
    // 精简版：只保留核心错误监控功能
    integrations: [
      // 捕获 console.error / warn 作为面包屑
      Sentry.captureConsoleIntegration({levels: ['error', 'warn']}),
      // 去重相同事件，降低噪音
      Sentry.dedupeIntegration(),
    ],
    // 保持信息清晰但不过深
    normalizeDepth: 5,
    maxBreadcrumbs: 50,
    attachStacktrace: true,
    maxValueLength: 1000,
    sampleRate: 1.0,
    tracesSampleRate: 0, // 关闭性能采样

    // 过滤低价值面包屑
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb?.category === 'console' && breadcrumb?.level === 'log') {
        return null;
      }
      if (breadcrumb?.category === 'xhr' && breadcrumb?.data?.status === 200) {
        return null;
      }
      return breadcrumb;
    },

    // 错误发送前的处理
    beforeSend(event, hint) {
      if (__DEV__) {
        console.log('📤 Sentry 准备发送事件:', event);
      }

      // 过滤掉一些不重要的错误
      if (event.exception) {
        const error = event.exception.values?.[0];
        if (error?.type === 'ChunkLoadError') {
          if (__DEV__) {
            console.log('🚫 过滤掉 ChunkLoadError');
          }
          return null; // 忽略 chunk 加载错误
        }
      }

      // 统一规范化 Axios 错误与未处理的 Promise 拒绝
      try {
        const original = (hint as any)?.originalException as any;
        const err: any = original || (event as any);

        // 基础脱敏 + 裁剪工具（限制深度与字符串长度）
        const redactKeys =
          /(password|passwd|authorization|cookie|token|secret|apikey|api_key)/i;
        const MAX_STRING = 500;
        const MAX_ARRAY = 20;
        const MAX_DEPTH = 3;
        const trimDeep = (value: any, depth: number = 0): any => {
          if (depth > MAX_DEPTH) {
            return '[TruncatedDepth]';
          }
          if (value == null) {
            return value;
          }
          const t = typeof value;
          if (t === 'string') {
            return value.length > MAX_STRING
              ? value.slice(0, MAX_STRING) + `…(${value.length - MAX_STRING})`
              : value;
          }
          if (t === 'number' || t === 'boolean') {
            return value;
          }
          if (Array.isArray(value)) {
            const limited = value
              .slice(0, MAX_ARRAY)
              .map(v => trimDeep(v, depth + 1));
            if (value.length > MAX_ARRAY) {
              limited.push(`[+${value.length - MAX_ARRAY} more]`);
            }
            return limited;
          }
          if (t === 'object') {
            const out: any = {};
            for (const k of Object.keys(value)) {
              out[k] = redactKeys.test(k)
                ? '[REDACTED]'
                : trimDeep((value as any)[k], depth + 1);
            }
            return out;
          }
          try {
            return String(value);
          } catch {
            return '[Unserializable]';
          }
        };

        // 1) 过滤来自外部 H5/WebView 的错误：请求 URL 不属于当前站点域名
        const eventUrl: string | undefined = (event as any)?.request?.url;
        if (
          typeof window !== 'undefined' &&
          eventUrl &&
          /^https?:/i.test(eventUrl)
        ) {
          try {
            const resolved = new URL(eventUrl, window.location.origin);
            if (resolved.host && resolved.host !== window.location.host) {
              if (__DEV__) {
                console.log('🚫 过滤 WebView/外部页面错误:', {url: eventUrl});
              }
              return null;
            }
          } catch (_) {
            // ignore URL parsing errors
          }
        }

        const isAxios =
          !!err?.isAxiosError ||
          !!err?.config ||
          !!err?.response ||
          (typeof err?.status === 'number' && err?.data);
        if (isAxios) {
          // 兼容 AxiosError（err.response）与 AxiosResponse（被业务拦截器 Promise.reject(response) 抛出）
          const httpStatus = err?.response?.status ?? err?.status;
          const responseData = err?.response?.data ?? err?.data;
          const method = err?.config?.method;
          const url = err?.config?.url;
          const code = err?.code;
          // 过滤 HTTP 200 但被业务拦截器判定为失败的情况（业务错误不上报）
          if (httpStatus === 200 && responseData) {
            const businessCode =
              getBusinessCode(responseData) ?? getBusinessStatus(responseData);
            const isBusinessSuccess =
              isStandardBusinessSuccess(responseData) || businessCode === 200;
            if (businessCode !== undefined && !isBusinessSuccess) {
              if (__DEV__) {
                console.log('🚫 过滤掉 HTTP 200 但业务响应被拦截的错误:', {
                  url,
                  businessCode,
                  businessStatus: getBusinessStatus(responseData),
                });
              }
              return null; // 忽略业务错误
            }
          }

          event.tags = {
            ...(event.tags || {}),
            http_status: String(httpStatus || ''),
            http_method: method || '',
            axios_code: code || '',
          };
          event.request = {...(event as any).request, method, url};
          const slimData = trimDeep(responseData);
          event.extra = {
            ...(event.extra || {}),
            axios: {url, method, status: httpStatus, code, data: slimData},
          };

          const msg = `AxiosError ${httpStatus || ''} ${
            method?.toUpperCase?.() || ''
          } ${url || ''}`.trim();
          if (event.exception?.values?.[0]) {
            event.exception.values[0].value = msg;
          } else {
            (event as any).message = msg;
          }

          // 指纹：接口路径 + 状态码 + 方法
          const path = (url || '').replace(/\?.*$/, '');
          (event as any).fingerprint = [
            'axios',
            String(httpStatus || '0'),
            method || '',
            path,
          ];
        } else if (original && !(original instanceof Error)) {
          // 非 Error 的 Promise rejection
          let safe: string;
          if (typeof original === 'string') {
            safe = original as string;
          } else {
            try {
              safe = JSON.stringify(trimDeep(original));
            } catch {
              safe = '[UnserializableRejection]';
            }
          }
          (event as any).message = `UnhandledRejection: ${safe}`;
          (event as any).fingerprint = [
            'unhandledrejection',
            safe.slice(0, 120),
          ];
        }
      } catch (_) {
        // 忽略规范化过程中可能的异常
      }
      return event;
    },
  });

  if (__DEV__) {
    console.log('✅ Sentry 初始化完成！');
  }
}

/**
 * 设置用户信息
 */
export function setSentryUser(user: {
  id: string;
  username?: string;
  email?: string;
}) {
  Sentry.setUser(user);
}

/**
 * 手动捕获错误
 */
export function captureError(error: Error, context?: any) {
  if (__DEV__) {
    console.log('🚨 手动上报错误到 Sentry:', {
      error: error.message,
      stack: error.stack,
      context,
    });
  }

  Sentry.captureException(error, {
    tags: {
      component: context?.component || 'unknown',
    },
    extra: context,
  });

  if (__DEV__) {
    console.log('✅ 错误已上报到 Sentry');
  }
}

/**
 * 手动捕获消息
 */
export function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' | 'debug' = 'info',
) {
  if (__DEV__) {
    console.log('📝 手动上报消息到 Sentry:', {
      message,
      level,
    });
  }

  Sentry.captureMessage(message, level);

  if (__DEV__) {
    console.log('✅ 消息已上报到 Sentry');
  }
}

/**
 * 设置标签
 */
export function setSentryTag(key: string, value: string) {
  Sentry.setTag(key, value);
}

/**
 * 设置上下文
 */
export function setSentryContext(key: string, context: any) {
  Sentry.setContext(key, context);
}

type SentryEventType = 'exception' | 'message';

interface SentryEventOptions {
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  contexts?: Record<string, Record<string, unknown>>;
  user?: {
    id?: string | number;
    email?: string;
    ip_address?: string;
    username?: string;
    [key: string]: unknown;
  };
  fingerprint?: string[];
  level?: 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug';
}

/**
 * 上报自定义事件到 Sentry，并为本次事件设置标签
 */
export function reportSentryEvent(
  type: SentryEventType,
  payload: Error | string,
  options?: SentryEventOptions,
) {
  if (options?.tags) {
    Sentry.withScope(scope => {
      Object.entries(options.tags!).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
      if (type === 'exception') {
        Sentry.captureException(payload, options);
      } else if (type === 'message') {
        Sentry.captureMessage(payload as string, options);
      }
    });
  } else {
    if (type === 'exception') {
      Sentry.captureException(payload, options);
    } else if (type === 'message') {
      Sentry.captureMessage(payload as string, options);
    }
  }
}
